/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * REFERRAL PROGRAM
 * ------------------------------------------------------------------------------------------------------------
 *
 * Web Component für die "Freunde empfehlen" Funktion.
 * Hört auf das "customer-data:loaded" Event von CustomerAccountNavigation
 * und zeigt die persönliche Referral-URL mit Share-Buttons an.
 */
class ReferralProgram extends HTMLElement {
  constructor() {
    super();
    this.onDataLoadedHandler = this._onDataLoaded.bind(this);
  }

  connectedCallback() {
    document.addEventListener('customer-data:loaded', this.onDataLoadedHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('customer-data:loaded', this.onDataLoadedHandler);
  }

  _onDataLoaded(event) {
    const customer = event.detail?.customer;
    if (!customer) return;

    const referralUrls = customer.referral_urls;
    const mainContent = this.querySelector('.referral-program');
    const unavailable = this.querySelector('.referral-program__unavailable');

    if (!referralUrls) {
      if (unavailable) unavailable.hidden = false;
      return;
    }

    if (mainContent) mainContent.hidden = false;

    this._populateUrls(referralUrls);
    this._setupCopyButton(referralUrls.direct);
    this._populateRemainingText(customer);
  }

  _populateUrls(referralUrls) {
    const urlInput = this.querySelector('[referral-url]');
    if (urlInput) urlInput.value = referralUrls.direct;
  }

  _setupCopyButton(directUrl) {
    const copyButton = this.querySelector('[referral-copy]');
    if (!copyButton) return;

    copyButton.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(directUrl);
        const label = copyButton.querySelector('.referral-program__copy-label');

        if (label) {
          label.hidden = false;
          setTimeout(() => { label.hidden = true; }, 2000);
        }
      } catch (err) {
        /* Fallback: Input selektieren */
        const urlInput = this.querySelector('[referral-url]');
        if (urlInput) {
          urlInput.select();
        }
      }
    });
  }

  _populateRemainingText(customer) {
    const remainingEl = this.querySelector('[referral-remaining]');
    if (!remainingEl) return;

    const referralRule = customer.available_rules?.find(rule => rule.kind === 'referral');
    if (!referralRule) {
      remainingEl.hidden = true;
      return;
    }

    const completionLimit = referralRule.context?.completion_limit;
    if (!completionLimit || completionLimit.state === 'no_limit') {
      remainingEl.hidden = true;
      return;
    }

    const interval = referralRule.limit?.interval;
    const intervalLabels = { month: this.dataset.intervalMonth, year: this.dataset.intervalYear };
    const intervalLabel = intervalLabels[interval] || interval;

    const remaining = completionLimit.remaining_completions_within_interval ?? 0;
    const isLimitReached = completionLimit.state === 'limit_reached_until_reset' || remaining === 0;

    const template = isLimitReached
      ? this.dataset.remainingNoneTemplate
      : this.dataset.remainingTemplate;

    remainingEl.textContent = template
      .replace('{{ remaining }}', remaining)
      .replace('{{ interval }}', intervalLabel);
  }
}

if (!customElements.get('referral-program')) {
  customElements.define('referral-program', ReferralProgram);
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * AdventCalendarWinnings
 * ------------------------------------------------------------------------------------------------------------
 *
 * Web Component für den Adventskalender-Gewinnspiel-Flow.
 * Rotiert mögliche Gewinne in der Eingabe-Ansicht, sendet den Gewinncode an eine externe API
 * und zeigt das passende Gewinn-Panel mit Rabattcode an.
 *
 * Erwartet API-Response: { handle: '...', discount_code: '...' }
 */
class AdventCalendarWinnings extends HTMLElement {
  connectedCallback() {
    this._prizeItems = Array.from(this.querySelectorAll('[data-prize-item]'));
    this._track = this.querySelector('[data-slider-track]');
    this._rotationIntervalMs = (parseInt(this.dataset.rotationInterval) || 5) * 1000;
    this._scratchColor = this.dataset.scratchColor || '#c8102e';
    this._currentRealIndex = 0;
    this._rotationTimeout = null;
    this._animationFrame = null;

    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    console.log('URL-Parameter:', params);
    if (params.prize) {
      /* Direktes Ansteuern eines Gewinn-Panels über URL-Parameter (z.B. nach Einlösen eines Codes) */
      this._showPrize(params.prize, params.discount, false);
    }

    const contentBox = this.querySelector(".advent-calendar__content-box");
    if (contentBox) {
      contentBox.classList.add("advent-calendar__content-box--visible");
    }

    this._initCodeSegments();

    const submitButton = this.querySelector('[data-submit-button]');
    if (submitButton) {
      this._submitHandler = () => this._submitCode();
      submitButton.addEventListener('click', this._submitHandler);
    }

    this._backHandler = (event) => {
      if (event.target.closest('[data-back-button]')) {
        this._resetToInputView();
      }
    };
    this.addEventListener('click', this._backHandler);

    /* Slider nur starten wenn mindestens 2 Items vorhanden */
    if (!this._track || this._prizeItems.length < 2) {
      return;
    }

    /* Clones für nahtlosen Endless-Loop:
       - vorne ein Clone des letzten Items, damit der linke Rand-Peek
         schon beim Seitenladen gefüllt ist
       - hinten Clones des ersten UND zweiten Items, damit beim Sprung
         vom Ende zum Anfang auch der rechte Rand-Peek nie leer ist */
    const lastClone = this._createSlideClone(this._prizeItems[this._prizeItems.length - 1]);
    const firstClone = this._createSlideClone(this._prizeItems[0]);
    const secondClone = this._createSlideClone(this._prizeItems[1]);

    this._track.prepend(lastClone);
    this._track.append(firstClone, secondClone);

    /* _allSlides: [lastClone, ...echteItems, firstClone, secondClone] */
    this._allSlides = [lastClone, ...this._prizeItems, firstClone, secondClone];

    /* DOM-Index des End-Clones des ersten Items – Ziel beim Endless-Wrap */
    this._firstCloneIndex = this._prizeItems.length + 1;

    /* Nach erstem Paint initialisieren damit Layout-Dimensionen verfügbar sind */
    requestAnimationFrame(() => {
      /* Alle Canvas-Overlays füllen – echte Items UND Clones, damit die
         Rand-Peeks und der Wrap-Sprung durchgängig rot überdeckt aussehen */
      this._allSlides.forEach((slide) => {
        const canvas = slide.querySelector('[data-scratch-canvas]');
        if (canvas) this._fillCanvas(canvas);
      });

      /* Erstes echtes Item zentrieren (Index 1 in _allSlides) */
      this._slideTo(1, false);
      this._startRotation();
    });

    const copyBtnWrappers = this.querySelectorAll('.redeem-popup__code-field');
    copyBtnWrappers.forEach((wrapper) => {
      const codeField = wrapper.querySelector('.redeem-popup__code');
      const button = wrapper.querySelector('.redeem-popup__copy-button');
      const newCopyLabel = wrapper.querySelector('.redeem-popup__copy-label');
      button.addEventListener('click', () => {
        navigator.clipboard.writeText(codeField.textContent).then(() => {
          newCopyLabel.hidden = false;
          setTimeout(() => { newCopyLabel.hidden = true; }, 2000);
        });
      });
    });
  }

  /* Erstellt einen für Screenreader versteckten Clone eines Slides
     für den Endless-Loop */
  _createSlideClone(item) {
    const clone = item.cloneNode(true);
    clone.setAttribute('data-clone', 'true');
    clone.setAttribute('aria-hidden', 'true');
    return clone;
  }

  disconnectedCallback() {
    this._stopRotation();

    const submitButton = this.querySelector('[data-submit-button]');
    if (submitButton && this._submitHandler) {
      submitButton.removeEventListener('click', this._submitHandler);
    }

    if (this._backHandler) {
      this.removeEventListener('click', this._backHandler);
    }
  }

  /* Slide zu einem bestimmten DOM-Index im _allSlides-Array verschieben */
  _slideTo(domIndex, animated = true) {
    if (!this._track || !this._allSlides) return;

    const container = this.querySelector('[data-prizes-container]');
    if (!container) return;

    if (!animated) {
      this._track.style.transition = 'none';
    }

    const slide = this._allSlides[domIndex];
    const containerWidth = container.offsetWidth;
    const slideLeft = slide.offsetLeft;
    const slideWidth = slide.offsetWidth;
    const translateX = containerWidth / 2 - slideLeft - slideWidth / 2;

    this._track.style.transform = `translateX(${translateX}px)`;

    if (!animated) {
      /* Reflow erzwingen damit transition:none sofort greift */
      void this._track.offsetHeight;
      this._track.style.transition = '';
    }
  }

  _initCodeSegments() {
    this._codeCells = Array.from(this.querySelectorAll('[data-code-cell]'));

    if (!this._codeCells.length) return;

    this._codeCells.forEach((cell, index) => {
      cell.addEventListener('input', (event) => this._handleCellInput(event, cell, index));
      cell.addEventListener('keydown', (event) => this._handleCellKeydown(event, cell, index));
      cell.addEventListener('click', () => this._focusNextEmptyCell());
    });
  }

  _handleCellInput(event, cell, index) {
    const filtered = cell.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    cell.value = filtered ? filtered[filtered.length - 1] : '';

    if (cell.value) {
      cell.classList.add('advent-calendar__code-cell--filled');
      const nextCell = this._codeCells[index + 1];
      if (nextCell) nextCell.focus();
    } else {
      cell.classList.remove('advent-calendar__code-cell--filled');
    }

    this._syncHiddenInput();
  }

  _handleCellKeydown(event, cell, index) {
    if (event.key === 'Backspace') {
      event.preventDefault();

      if (cell.value) {
        cell.value = '';
        cell.classList.remove('advent-calendar__code-cell--filled');
      } else {
        const prevCell = this._codeCells[index - 1];
        if (prevCell) {
          prevCell.value = '';
          prevCell.classList.remove('advent-calendar__code-cell--filled');
          prevCell.focus();
        }
      }

      this._syncHiddenInput();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      const prevCell = this._codeCells[index - 1];
      if (prevCell) prevCell.focus();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      const nextCell = this._codeCells[index + 1];
      if (nextCell) nextCell.focus();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this._submitCode();
    }
  }

  _focusNextEmptyCell() {
    const firstEmpty = this._codeCells.find((cell) => !cell.value);
    if (firstEmpty) firstEmpty.focus();
  }

  _syncHiddenInput() {
    const codeInput = this.querySelector('[data-code-input]');
    if (!codeInput) return;

    const allFilled = this._codeCells.every((cell) => cell.value);

    if (!allFilled) {
      codeInput.value = '';
      return;
    }

    const format = this.dataset.codeFormat || 'XXXXX-XXXXX';
    const formatChars = format.split('');
    let cellIndex = 0;

    const codeParts = formatChars.map((char) => {
      if (char === 'X') {
        const cellValue = this._codeCells[cellIndex] ? this._codeCells[cellIndex].value : '';
        cellIndex++;
        return cellValue;
      }
      return char;
    });

    codeInput.value = codeParts.join('');
  }

  _startRotation() {
    /* Erstes zentriertes Item direkt animieren – nach kurzem Settle */
    this._rotationTimeout = setTimeout(() => this._runScratchCycle(), 300);
  }

  _advanceSlide() {
    if (!this._allSlides) return;

    /* Aktuelles Item wird inaktiv: Canvas sofort wieder mit dem Overlay
       füllen, damit es beim nächsten Auftauchen erneut bei 0 startet */
    const outgoingCanvas = this._prizeItems[this._currentRealIndex].querySelector('[data-scratch-canvas]');
    if (outgoingCanvas) this._fillCanvas(outgoingCanvas);

    const nextRealIndex = (this._currentRealIndex + 1) % this._prizeItems.length;

    /* Beim Übergang vom letzten zum ersten echten Item: zum End-Clone gleiten,
       dann silent jump zum echten ersten Item – so entsteht der nahtlose Endless-Loop */
    let domIndex;
    if (nextRealIndex === 0) {
      domIndex = this._firstCloneIndex; /* End-Clone des ersten Items */
    } else {
      domIndex = nextRealIndex + 1;
    }

    this._slideTo(domIndex, true);

    /* Nach Transition-Ende: silent jump falls Clone, dann Animation starten */
    setTimeout(() => {
      if (domIndex === this._firstCloneIndex) {
        /* Silent jump: End-Clone → erstes echtes Item */
        this._slideTo(1, false);
      }

      this._currentRealIndex = nextRealIndex;
      this._runScratchCycle();
    }, 600);
  }

  /* Rubbellos-Runde für das aktuell zentrierte Item: animieren,
     danach den nächsten Slide-Wechsel einplanen */
  _runScratchCycle() {
    const activeCanvas = this._prizeItems[this._currentRealIndex].querySelector('[data-scratch-canvas]');

    this._playActiveAnimation(activeCanvas).then(() => {
      this._scheduleNextAdvance();
    });
  }

  _scheduleNextAdvance() {
    this._rotationTimeout = setTimeout(
      () => this._advanceSlide(),
      this._rotationIntervalMs
    );
  }

  /* Rubbellos-Animation: progressives Wegkratzen des Overlays (1500ms) */
  _playActiveAnimation(canvas) {
    return new Promise((resolve) => {
      if (!canvas) {
        resolve();
        return;
      }

      /* Canvas neu füllen falls Dimensionen beim Init noch 0 waren */
      if (canvas.width === 0 || canvas.height === 0) {
        this._fillCanvas(canvas);
      }

      const ctx = canvas.getContext('2d');
      const startTime = performance.now();
      const duration = 900;
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      /* Kratzbereich: 8%–92% der Breite, ein schmaler Rand bleibt stehen
         – wie bei einem echten Rubbellos */
      const scratchLeft = canvasWidth * 0.08;
      const scratchRight = canvasWidth * 0.92;

      /* Serpentinen-Pfad: ein virtueller Finger wandert in mehreren Reihen
         abwechselnd nach links und rechts über die Karte */
      const rows = 3;
      const rowHeight = canvasHeight / rows;
      const brushRadius = rowHeight * 0.7;

      /* destination-out löscht vorhandene Pixel statt neue zu zeichnen –
         dadurch wird das Overlay freigerubbelt (Technik aus dem Blog-Beispiel).
         lineTo-Striche mit runder Kappe ergeben eine zusammenhängende Spur. */
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = brushRadius * 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      /* easeInOutQuad: weiches Beschleunigen und Abbremsen statt linearem Lauf */
      const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

      /* Pinsel-Position für einen Fortschrittswert (0–1) entlang der Serpentine */
      const getBrushPosition = (t) => {
        const rowProgress = t * rows;
        const rowIndex = Math.min(Math.floor(rowProgress), rows - 1);
        const withinRow = rowProgress - rowIndex;
        const movingRight = rowIndex % 2 === 0;

        const x = movingRight
          ? scratchLeft + (scratchRight - scratchLeft) * withinRow
          : scratchRight - (scratchRight - scratchLeft) * withinRow;

        /* Reihenmitte plus leichte Sinus-Welle für eine organische Bewegung */
        const rowCenter = rowHeight * (rowIndex + 0.5);
        const wave = Math.sin(withinRow * Math.PI * 2) * canvasHeight * 0.05;

        return { x: x, y: rowCenter + wave };
      };

      let previousPoint = getBrushPosition(0);

      const scratchLoop = (now) => {
        const elapsed = now - startTime;
        const linearProgress = Math.min(elapsed / duration, 1);
        const progress = easeInOutQuad(linearProgress);

        const currentPoint = getBrushPosition(progress);

        /* Verbundenen Kratzstrich vom letzten zum aktuellen Punkt ziehen */
        ctx.beginPath();
        ctx.moveTo(previousPoint.x, previousPoint.y);
        ctx.lineTo(currentPoint.x, currentPoint.y);
        ctx.stroke();

        /* Kreis am aktuellen Punkt für eine gleichmäßig runde Spurspitze */
        ctx.beginPath();
        ctx.arc(currentPoint.x, currentPoint.y, brushRadius, 0, Math.PI * 2);
        ctx.fill();

        previousPoint = currentPoint;

        if (linearProgress < 1) {
          this._animationFrame = requestAnimationFrame(scratchLoop);
        } else {
          this._animationFrame = null;
          resolve();
        }
      };

      this._animationFrame = requestAnimationFrame(scratchLoop);
    });
  }

  /* Canvas mit der konfigurierten Scratch-Farbe füllen */
  _fillCanvas(canvas) {
    if (!canvas || canvas.offsetWidth === 0) return;

    canvas.width = canvas.offsetWidth * (window.devicePixelRatio || 1);
    canvas.height = canvas.offsetHeight * (window.devicePixelRatio || 1);

    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = this._scratchColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    /* CSS-Vorab-Overlay entfernen – ab jetzt deckt das Canvas-Bitmap selbst ab.
       Nötig, damit das Wegrubbeln (destination-out) den Gewinn freilegt
       und nicht nur den CSS-Hintergrund */
    canvas.style.backgroundColor = 'transparent';
  }

  _stopRotation() {
    if (this._rotationTimeout) {
      clearTimeout(this._rotationTimeout);
      this._rotationTimeout = null;
    }

    if (this._animationFrame) {
      cancelAnimationFrame(this._animationFrame);
      this._animationFrame = null;
    }
  }

  async _submitCode() {
    const codeInput = this.querySelector('[data-code-input]');
    const errorMessage = this.querySelector('[data-error-message]');
    const submitButton = this.querySelector('[data-submit-button]');
    
    const code = codeInput ? codeInput.value.trim() : '';
    
    if (!code) {
      if (errorMessage) errorMessage.hidden = false;
      return;
    }
    
    if (errorMessage) errorMessage.hidden = true;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
    } 
      

    try {
      /* API-URL und Parameter aus data-Attributen lesen */
      const apiUrl = "https://us-central1-shopify-giftcard-migration.cloudfunctions.net/lookupWinCode"
      const fileName = this.dataset.fileName || 'adventscalendar-test-codes';
      const url = `${apiUrl}?code=${encodeURIComponent(code)}&file=${encodeURIComponent(fileName)}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      const handle = data.metaobject_handle;
      const discountCode = data.discount;

      if (!handle) {
        throw new Error('No handle in API response');
      }

      this._showPrize(handle, discountCode);
    } catch (error) {
      if (errorMessage) errorMessage.hidden = false;
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
      }
    }
  }

  _showPrize(handle, discountCode, firstShow = true) {
    this._stopRotation();

    const codeForm = this.querySelector('[data-code-form]');
    const bodyText = this.querySelector('[data-body-text]');
    const prizesContainer = this.querySelector('[data-prizes-container]');
    const prizeView = this.querySelector('[data-prize-view]');
    const addToCartButton = this.querySelector(`[data-add-to-cart][data-handle="${handle}"]`);

    if (codeForm) codeForm.hidden = true;
    if (bodyText) bodyText.hidden = true;
    if (prizesContainer) prizesContainer.hidden = true;
    if (prizeView) prizeView.hidden = false;

    if (addToCartButton) {
      const discountUrlParameter = addToCartButton.getAttribute('discount-url-parameter') || '';
      addToCartButton.setAttribute('discount-url-parameter', `${discountUrlParameter}&discount=${encodeURIComponent(discountCode)}`);
      addToCartButton.setAttribute('discount-code', discountCode);
    }

    const panel = prizeView
      ? prizeView.querySelector(`[data-prize-panel][data-handle="${handle}"]`)
      : null;

    if (panel) {
      panel.hidden = false;

      const discountCodeEl = panel.querySelector('[data-discount-code]');
      const discountWrapper = panel.querySelector('[data-discount-wrapper]');

      if (discountCode && discountCodeEl) {
        discountCodeEl.textContent = discountCode;
      }

      if (discountCode && discountWrapper) {
        discountWrapper.hidden = false;
      }

      const discountLink = panel.querySelector('[data-discount-link]');
      if (discountCode && discountLink) {
        let link = discountLink.getAttribute("data-url");
        link = link.replace("[discount]", discountCode)
        discountLink.setAttribute("href", link);
      }

      if (!firstShow) {
        const cartButton = panel.querySelector(`[data-add-to-cart]`);
        const successMessage = panel.querySelector(`.advent-calendar__prize-buttons .success_button`);
        if (cartButton && successMessage) {
          cartButton.hidden = true;
          successMessage.hidden = false;
        }
      }
    }

    if (firstShow) {
      this._startConfetti();
    }
  }

  _resetToInputView() {
    const prizeView = this.querySelector('[data-prize-view]');
    const inputView = this.querySelector('[data-input-view]');
    const codeForm = this.querySelector('[data-code-form]');
    const prizesContainer = this.querySelector('[data-prizes-container]');
    const codeInput = this.querySelector('[data-code-input]');
    const errorMessage = this.querySelector('[data-error-message]');
    const submitButton = this.querySelector('[data-submit-button]');

    /* Alle Prize Panels ausblenden */
    const panels = this.querySelectorAll('[data-prize-panel]');
    panels.forEach((panel) => { panel.hidden = true; });

    /* Discount Wrapper zurücksetzen */
    const discountWrappers = this.querySelectorAll('[data-discount-wrapper]');
    discountWrappers.forEach((wrapper) => { wrapper.hidden = true; });

    if (prizeView) prizeView.hidden = true;
    if (inputView) inputView.hidden = false;
    if (codeForm) codeForm.hidden = false;
    if (prizesContainer) prizesContainer.hidden = false;
    if (errorMessage) errorMessage.hidden = true;
    if (submitButton) submitButton.disabled = false;

    /* Alle echten Canvas-Overlays zurücksetzen */
    this._prizeItems.forEach((item) => {
      const canvas = item.querySelector('[data-scratch-canvas]');
      if (canvas) this._fillCanvas(canvas);
    });
      
    /* Segmented Code Input zurücksetzen */
    if (this._codeCells) {
      this._codeCells.forEach((cell) => {
        cell.value = '';
        cell.classList.remove('advent-calendar__code-cell--filled');
      });
    }
    
    if (codeInput) codeInput.value = '';

    /* Rotation neu starten – aktuellen Index zurücksetzen */
    this._currentPrizeIndex = 0;
    this._prizeItems.forEach((item, index) => {
      item.hidden = index !== 0;
    });

    /* Slider zum ersten echten Item zurückspringen */
    this._currentRealIndex = 0;
    this._slideTo(1, false);
    this._startRotation();
  }

  /* Konfetti-Regen beim Anzeigen der Preisansicht – läuft 4 Sekunden,
     dann Fade-out. Canvas wird auf den Content-Box-Bereich geclippt. */
  _startConfetti() {
    /* Ein rAF sicherstellt dass das Layout berechnet ist (z.B. bei URL-Parameter-Flow) */
    requestAnimationFrame(() => {
      const canvas = this.querySelector('[data-confetti-canvas]');
      if (!canvas) return;

      const canvasRect = canvas.getBoundingClientRect();
      canvas.width = canvasRect.width;
      canvas.height = canvasRect.height;

      const ctx = canvas.getContext('2d');

      /* Konfetti-Bereich aus Content-Box ermitteln, Fallback: gesamte Section */
      const contentBox = this.querySelector('.advent-calendar__content-box');
      const boundsEl = contentBox || this;
      const boundsRect = boundsEl.getBoundingClientRect();

      const clipLeft = boundsRect.left - canvasRect.left;
      const clipTop = boundsRect.top - canvasRect.top;
      const clipRight = boundsRect.right - canvasRect.left;
      const clipBottom = boundsRect.bottom - canvasRect.top;
      const clipWidth = clipRight - clipLeft;
      const clipHeight = clipBottom - clipTop;

      const colors = ['#e63946', '#f4a261', '#2a9d8f', '#e9c46a', '#ffffff', '#264653'];

      /* 80 Partikel mit zufälligen Eigenschaften */
      const particles = Array.from({ length: 80 }, () => ({
        x: clipLeft + Math.random() * clipWidth,
        y: clipTop - 20 - Math.random() * 80,
        w: 6 + Math.random() * 8,
        h: 8 + Math.random() * 10,
        color: colors[Math.floor(Math.random() * colors.length)],
        speedY: 2 + Math.random() * 3,
        speedX: (Math.random() - 0.5) * 3,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 6,
        opacity: 1,
      }));

      const duration = 4000;
      const fadeStart = duration - 800;
      const startTime = performance.now();

      const loop = (now) => {
        const elapsed = now - startTime;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.save();
        ctx.beginPath();
        ctx.rect(clipLeft, clipTop, clipWidth, clipHeight);
        ctx.clip();

        /* Globale Opacity für Fade-out in den letzten 800ms */
        const globalOpacity = elapsed >= fadeStart
          ? 1 - (elapsed - fadeStart) / 800
          : 1;

        particles.forEach((p) => {
          p.x += p.speedX;
          p.y += p.speedY;
          p.rotation += p.rotationSpeed;

          /* Partikel die den Bereich verlassen oben neu einsetzen */
          if (p.y - p.h > clipBottom) {
            p.y = clipTop - p.h;
            p.x = clipLeft + Math.random() * clipWidth;
          }

          ctx.save();
          ctx.globalAlpha = globalOpacity;
          ctx.translate(p.x, p.y);
          ctx.rotate((p.rotation * Math.PI) / 180);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
          ctx.restore();
        });

        ctx.restore();

        if (elapsed < duration) {
          requestAnimationFrame(loop);
        } else {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      };

      requestAnimationFrame(loop);
    });
  }
}

if (!customElements.get('advent-calendar-winnings')) {
  customElements.define('advent-calendar-winnings', AdventCalendarWinnings);
}

// /* =========================================
//  * ArticleImageZoom
//  * Adds a zoom-button overlay to each RTE image in blog articles.
//  * On click, opens a full-width modal with zoom-in/out controls.
//  * ========================================= */
// class ArticleImageZoom extends HTMLElement {
//   connectedCallback() {
//     this._modal = this.querySelector('#article-image-zoom-modal');
//     this._modalImage = this.querySelector('.article-image-zoom__image');
//     this._zoomLevelDisplay = this.querySelector('.article-image-zoom__zoom-level');
//     this._triggerTemplate = this.querySelector('template[data-pattern="image-zoom-trigger"]');
//     this._currentZoom = 100;
//     this._maxZoom = 100;

//     this._wrapImages();
//     this._bindModalControls();
//   }

//   _wrapImages() {
//     const articleContent = this.closest('.article__content');
//     if (!articleContent) return;

//     const images = articleContent.querySelectorAll('.rte img');
//     images.forEach((img) => {
//       /* Idempotent – skip already-wrapped images */
//       if (img.closest('.article-image-zoom__trigger')) return;

//       const wrapper = document.createElement('span');
//       wrapper.className = 'article-image-zoom__trigger';

//       img.parentNode.insertBefore(wrapper, img);
//       wrapper.appendChild(img);

//       if (this._triggerTemplate) {
//         const buttonClone = this._triggerTemplate.content.cloneNode(true);
//         wrapper.appendChild(buttonClone);

//         const button = wrapper.querySelector('.article-image-zoom__button');
//         if (button) {
//           button.addEventListener('click', (event) => {
//             event.stopPropagation();
//             this._openZoom(img);
//           });
//         }
//       }

//       img.addEventListener('click', () => this._openZoom(img));
//     });
//   }

//   _openZoom(img) {
//     if (!this._modal || !this._modalImage) return;

//     const highResSrc = this._getHighResSrc(img);
//     this._modalImage.src = highResSrc;
//     this._modalImage.alt = img.alt || '';
//     this._modalImage.removeAttribute('width');
//     this._modalImage.removeAttribute('height');

//     /* Calculate max zoom: ratio of native width to current display width */
//     const displayWidth = img.offsetWidth || img.width;
//     const naturalWidth = img.naturalWidth;
//     if (naturalWidth && displayWidth) {
//       this._maxZoom = Math.max(100, Math.round((naturalWidth / displayWidth) * 100));
//     } else {
//       this._maxZoom = 100;
//     }

//     /* Reset to 100% on each open */
//     this._currentZoom = 100;
//     this._applyZoom();

//     this._modal.open = true;
//   }

//   _getHighResSrc(img) {
//     const src = img.src || '';

//     /* Shopify CDN: remove size suffix like _200x.jpg or _200x300.jpg to get original */
//     const shopifyPattern = /(_\d+x\d*(?:_crop_\w+)?)(\.(?:jpg|jpeg|png|gif|webp))(\?.*)?$/i;
//     if (shopifyPattern.test(src)) {
//       return src.replace(shopifyPattern, '$2$3');
//     }

//     /* Fallback: pick the largest entry from srcset */
//     const srcset = img.srcset;
//     if (srcset) {
//       const entries = srcset.split(',').map((entry) => {
//         const parts = entry.trim().split(/\s+/);
//         return { url: parts[0], width: parts[1] ? parseInt(parts[1]) : 0 };
//       });
//       entries.sort((a, b) => b.width - a.width);
//       if (entries.length > 0 && entries[0].url) {
//         return entries[0].url;
//       }
//     }

//     return src;
//   }

//   _zoom(delta) {
//     this._currentZoom = Math.min(this._maxZoom, Math.max(100, this._currentZoom + delta));
//     this._applyZoom();
//   }

//   _applyZoom() {
//     if (this._modalImage) {
//       this._modalImage.style.transform = `scale(${this._currentZoom / 100})`;
//     }
//     if (this._zoomLevelDisplay) {
//       this._zoomLevelDisplay.textContent = `${this._currentZoom}%`;
//     }
//   }

//   _bindModalControls() {
//     const zoomInBtn = this.querySelector('[data-action="zoom-in"]');
//     const zoomOutBtn = this.querySelector('[data-action="zoom-out"]');

//     if (zoomInBtn) {
//       zoomInBtn.addEventListener('click', () => this._zoom(10));
//     }
//     if (zoomOutBtn) {
//       zoomOutBtn.addEventListener('click', () => this._zoom(-10));
//     }
//   }
// }

// if (!customElements.get('article-image-zoom')) {
//   customElements.define('article-image-zoom', ArticleImageZoom);

class MobileNavV2ThreeLayer extends HTMLElement {
  connectedCallback() {
    this._handleClick = this._handleClick.bind(this);
    this.addEventListener('click', this._handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._handleClick);
  }

  _handleClick(event) {
    const button = event.target.closest('.mobile-nav-v2__switch-button');
    if (!button) return;

    this.querySelectorAll('.mobile-nav-v2__switch-button').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });

    this.querySelectorAll('.mobile-nav-v2__layer-links').forEach((list) => {
      list.hidden = true;
    });

    button.setAttribute('aria-expanded', 'true');

    const possibleTargets = this.querySelectorAll(".mobile-nav-v2__first-layer");
    possibleTargets.forEach((target) => {
      target.hidden = true;
    });
    const targetId = button.getAttribute('aria-controls');
    const target = document.getElementById(targetId);
    console.log("targetId", targetId, target);
    if (target) {
      target.hidden = false;
    }
  }
}

if (!customElements.get('mobile-nav-v2-three-layer')) {
  customElements.define('mobile-nav-v2-three-layer', MobileNavV2ThreeLayer);
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * RecipeServingSelector
 * ------------------------------------------------------------------------------------------------------------
 *
 * Web Component für den Portions-Selektor auf Rezept-Seiten (sections/main-recipe.liquid).
 * Plus/Minus ändert die Portionsgröße (Minimum 1) und dispatcht ein document-weites
 * CustomEvent 'recipe:serving-size:changed' mit { servingSize, factor }.
 *
 * Der Selektor wird zweimal gerendert (Desktop-Box + Mobile-Collapsible). Jede Instanz
 * hört selbst auf das Event, damit beide Instanzen ohne ID-Coupling synchron bleiben.
 */
class RecipeServingSelector extends HTMLElement {
  connectedCallback() {
    this._defaultServingSize = parseInt(this.getAttribute('default-serving-size')) || 1;
    this._servingSize = this._defaultServingSize;
    this._valueElement = this.querySelector('.recipe-serving-selector__value');
    this._unitElement = this.querySelector('.recipe-serving-selector__unit');

    /* Button-Reihenfolge folgt dem Markup: erst Minus, dann Plus */
    const buttons = this.querySelectorAll('.quantity-selector__button');
    this._decreaseHandler = () => this._changeServingSize(-1);
    this._increaseHandler = () => this._changeServingSize(1);
    if (buttons[0]) {
      buttons[0].addEventListener('click', this._decreaseHandler);
    }
    if (buttons[1]) {
      buttons[1].addEventListener('click', this._increaseHandler);
    }

    this._servingSizeChangedHandler = (event) => this._render(event.detail.servingSize);
    document.addEventListener('recipe:serving-size:changed', this._servingSizeChangedHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('recipe:serving-size:changed', this._servingSizeChangedHandler);
  }

  _changeServingSize(delta) {
    const newServingSize = Math.max(1, this._servingSize + delta);
    if (newServingSize === this._servingSize) return;

    document.dispatchEvent(new CustomEvent('recipe:serving-size:changed', {
      detail: {
        servingSize: newServingSize,
        factor: newServingSize / this._defaultServingSize
      }
    }));
  }

  _render(servingSize) {
    this._servingSize = servingSize;

    if (this._valueElement) {
      this._valueElement.textContent = servingSize;
    }

    if (this._unitElement) {
      const unitSingular = this.getAttribute('unit-singular') || '';
      const unitPlural = this.getAttribute('unit-plural') || unitSingular;
      this._unitElement.textContent = servingSize === 1 ? unitSingular : unitPlural;
    }
  }
}

if (!customElements.get('recipe-serving-selector')) {
  customElements.define('recipe-serving-selector', RecipeServingSelector);
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * RecipeIngredientsScaler
 * ------------------------------------------------------------------------------------------------------------
 *
 * Web Component, die Zutatenmengen auf die gewählte Portionsgröße umrechnet.
 * Hört auf 'recipe:serving-size:changed' und aktualisiert alle Nachfahren mit
 * [data-base-amount] per textContent – das HTML lebt vollständig im Liquid.
 *
 * Skaliert werden nur eindeutig numerische Rohwerte (Ganzzahl, Dezimalzahl mit
 * Komma oder Punkt, einfache Brüche wie 1/2). Nicht parsebare Werte (z.B. "1-2",
 * "etwas") sowie Elemente mit data-unit-fixed bleiben unverändert.
 */
class RecipeIngredientsScaler extends HTMLElement {
  connectedCallback() {
    this._servingSizeChangedHandler = (event) => this._scaleAmounts(event.detail.factor);
    document.addEventListener('recipe:serving-size:changed', this._servingSizeChangedHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('recipe:serving-size:changed', this._servingSizeChangedHandler);
  }

  _scaleAmounts(factor) {
    this.querySelectorAll('[data-base-amount]').forEach((element) => {
      if (element.hasAttribute('data-unit-fixed')) return;

      const baseAmount = this._parseAmount(element.dataset.baseAmount);
      if (baseAmount === null) return;

      const scaledAmount = baseAmount * factor;
      const formattedAmount = this._formatAmount(scaledAmount);
      const unit = this._unitForAmount(element, scaledAmount);

      element.textContent = unit ? `${formattedAmount} ${unit}` : formattedAmount;
    });
  }

  /* Gibt den numerischen Wert zurück oder null wenn der Rohwert nicht eindeutig numerisch ist */
  _parseAmount(rawAmount) {
    const value = (rawAmount || '').trim();

    if (/^\d+\s*\/\s*\d+$/.test(value)) {
      const parts = value.split('/');
      const denominator = parseFloat(parts[1]);
      if (denominator === 0) return null;
      return parseFloat(parts[0]) / denominator;
    }

    if (/^\d+(?:[.,]\d+)?$/.test(value)) {
      return parseFloat(value.replace(',', '.'));
    }

    return null;
  }

  /* Deutsche Formatierung: Komma als Dezimaltrenner, max. 2 Nachkommastellen, ohne trailing zeros */
  _formatAmount(amount) {
    const rounded = Math.round(amount * 100) / 100;
    return String(rounded).replace('.', ',');
  }

  _unitForAmount(element, amount) {
    const unitSingular = element.dataset.unitSingular || '';
    const unitPlural = element.dataset.unitPlural || unitSingular;
    return amount === 1 ? unitSingular : unitPlural;
  }
}

if (!customElements.get('recipe-ingredients-scaler')) {
  customElements.define('recipe-ingredients-scaler', RecipeIngredientsScaler);
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * RecipeWakeLockToggle
 * ------------------------------------------------------------------------------------------------------------
 *
 * Web Component für den Wake-Lock-Switch über der Zutatenliste auf Rezept-Seiten
 * (sections/main-recipe.liquid). Solange der Switch aktiviert und die Seite sichtbar ist,
 * hält die Screen Wake Lock API das Display wach.
 *
 * Ohne Browser-Support oder ohne Secure Context bleibt das Element hidden (Progressive
 * Enhancement). Der Switch wird zweimal gerendert (Desktop-Box + Mobile-Collapsible).
 * Jede Instanz hört auf das document-weite CustomEvent 'recipe:wake-lock:changed', damit
 * beide Checkboxen synchron bleiben; Lock-Zustand und Sentinel liegen als static Members
 * auf der Klasse, damit instanzübergreifend nur ein Lock angefordert wird.
 */
class RecipeWakeLockToggle extends HTMLElement {
  static _sentinel = null;
  static _enabled = false;
  static _requestPending = false;
  static _connectedInstances = 0;

  connectedCallback() {
    if (!('wakeLock' in navigator)) return;
    if (!window.isSecureContext) return;

    this.removeAttribute('hidden');
    RecipeWakeLockToggle._connectedInstances += 1;

    this._checkbox = this.querySelector('.switch-checkbox');
    this._changeHandler = () => {
      document.dispatchEvent(new CustomEvent('recipe:wake-lock:changed', {
        detail: { enabled: this._checkbox.checked }
      }));
    };
    if (this._checkbox) {
      this._checkbox.addEventListener('change', this._changeHandler);
    }

    this._wakeLockChangedHandler = (event) => this._applyState(event.detail.enabled);
    document.addEventListener('recipe:wake-lock:changed', this._wakeLockChangedHandler);

    /* Der Browser gibt den Lock beim Tab-/App-Wechsel automatisch frei – bei Rückkehr erneut anfordern */
    this._visibilityChangeHandler = () => {
      if (document.visibilityState !== 'visible') return;
      if (!RecipeWakeLockToggle._enabled) return;
      RecipeWakeLockToggle._requestLock();
    };
    document.addEventListener('visibilitychange', this._visibilityChangeHandler);
  }

  disconnectedCallback() {
    /* connectedCallback hat ohne Wake-Lock-Support früh abgebrochen – nichts aufzuräumen */
    if (!this._wakeLockChangedHandler) return;

    if (this._checkbox) {
      this._checkbox.removeEventListener('change', this._changeHandler);
    }
    document.removeEventListener('recipe:wake-lock:changed', this._wakeLockChangedHandler);
    document.removeEventListener('visibilitychange', this._visibilityChangeHandler);

    RecipeWakeLockToggle._connectedInstances -= 1;
    if (RecipeWakeLockToggle._connectedInstances === 0) {
      RecipeWakeLockToggle._enabled = false;
      RecipeWakeLockToggle._releaseLock();
    }
  }

  /* Synchronisiert die eigene Checkbox; die erste Instanz, die den Zustandswechsel sieht, verwaltet den Lock */
  _applyState(enabled) {
    if (this._checkbox) {
      this._checkbox.checked = enabled;
    }

    if (RecipeWakeLockToggle._enabled === enabled) return;
    RecipeWakeLockToggle._enabled = enabled;

    if (enabled) {
      RecipeWakeLockToggle._requestLock();
    } else {
      RecipeWakeLockToggle._releaseLock();
    }
  }

  static async _requestLock() {
    if (RecipeWakeLockToggle._requestPending) return;
    if (RecipeWakeLockToggle._sentinel && RecipeWakeLockToggle._sentinel.released === false) return;

    RecipeWakeLockToggle._requestPending = true;
    try {
      const sentinel = await navigator.wakeLock.request('screen');
      if (RecipeWakeLockToggle._enabled) {
        RecipeWakeLockToggle._sentinel = sentinel;
      } else {
        /* Switch wurde während der Anfrage deaktiviert */
        sentinel.release();
      }
    } catch (error) {
      /* Anfrage abgelehnt (z.B. Energiesparmodus) – alle Checkboxen zurücksetzen */
      if (RecipeWakeLockToggle._enabled) {
        document.dispatchEvent(new CustomEvent('recipe:wake-lock:changed', {
          detail: { enabled: false }
        }));
      }
    } finally {
      RecipeWakeLockToggle._requestPending = false;
    }
  }

  static _releaseLock() {
    if (!RecipeWakeLockToggle._sentinel) return;

    RecipeWakeLockToggle._sentinel.release();
    RecipeWakeLockToggle._sentinel = null;
  }
}

if (!customElements.get('recipe-wake-lock-toggle')) {
  customElements.define('recipe-wake-lock-toggle', RecipeWakeLockToggle);
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * TEA NAV TRACKING
 * ------------------------------------------------------------------------------------------------------------
 *
 * Trackt die Tea-Switch Path Attribution der Header-v2-Navigation (Mobile + Desktop):
 *
 * 1. Nutzer wählt einen Tea-by-X-Switch (by_moment / by_type) und klickt im jeweiligen
 *    Modus auf einen Sublink -> GA4-Event "tea_nav_link_click".
 * 2. Ein nachfolgendes Add-to-Cart (Theme-Event "variant:added") wird dem Modus zugeordnet,
 *    solange die Kette intakt ist: Switch -> Sublink -> Produkt -> Add-to-Cart.
 *    Jede andere Menü-/Navigations-Interaktion bricht die Kette.
 *
 * Der Modus kommt aus dem data-nav-mode-Attribut, das im Liquid an den Switch-Buttons und
 * Layer-Listen gerendert wird (pt-mobile-menu-v2, pt-desktop-menu-item + Link-Wrapper).
 *
 * Ziele: GA4 (dataLayer), Microsoft Clarity (Custom Tags) und Shopify Cart-Attribute
 * ("tea_nav_mode_<variantId>", eine Note pro Produkt, da eine Order mehrere Modi enthalten
 * kann; Weiterverarbeitung zu Order-Tags übernimmt Shopify Flow).
 *
 * Zuverlässigkeit: GA4-Events laufen über eine sessionStorage-Outbox und werden erst nach
 * GTM-Bestätigung (eventCallback) entfernt. Passiert direkt nach einem Event ein Reload
 * oder Seitenwechsel, pusht die Folgeseite offene Einträge erneut (Dedup über
 * tea_nav_event_id). Der Cart-Attribut-Request nutzt keepalive und überlebt so einen
 * sofortigen Seitenwechsel.
 */

// Debug-Modus: auf true setzen für ausführliche Konsolen-Logs.
const TEA_NAV_TRACKING_DEBUG = true;

class TeaNavTracking {
  static SELECTORS = {
    switchButton: '.mobile-nav-v2__switch-button, .mega-menu-v2__third-layer-link',
    subLink: 'a.mobile-nav-v2__sublink, a.mega-menu-v2__sublink',
    // Brechen die Kette immer, auch wenn sie auf ein Produkt zeigen (Promo-Kacheln im Mega-Menü):
    forceChainBreakers: '.mega-menu-v2__featured-item'
  };

  static ATTRIBUTION_KEY = 'pt_nav_attr';
  static MODE_KEY = 'pt_nav_mode';
  static QUEUE_KEY = 'pt_nav_event_queue';
  static ATTRIBUTION_TTL_MS = 30 * 60 * 1000;
  // Ein Attempt = ein Push pro Seitenaufruf (Klick-Seite + Folgeseiten via Flush).
  // 4 gibt dem Event also bis zu 3 Folgeseiten Zeit, bis GTM bestätigt hat.
  static MAX_PUSH_ATTEMPTS = 4;
  static MAX_QUEUE_SIZE = 20;
  static EVENT_TIMEOUT_MS = 2000;

  constructor() {
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onVariantAdded = this._onVariantAdded.bind(this);

    // Capture-Phase: läuft vor den Navigations-Komponenten (DesktopNavigation,
    // MobileNavV2ThreeLayer) und ist immun gegen stopPropagation in deren Handlern.
    document.addEventListener('click', this._onDocumentClick, true);
    document.addEventListener('variant:added', this._onVariantAdded);

    // Events, die auf der Vorseite nicht mehr bestätigt wurden, erneut pushen.
    this._flushEventQueue();

    this._log('Init OK.');
  }

  _onDocumentClick(event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    const switchButton = target.closest(TeaNavTracking.SELECTORS.switchButton);
    if (switchButton) {
      this._onSwitchClick(switchButton);
      return;
    }

    const subLink = target.closest(TeaNavTracking.SELECTORS.subLink);
    if (subLink) {
      this._onSublinkClick(subLink);
      return;
    }

    // Jeder andere Navigations-Klick, der nicht Teil des erlaubten Pfads ist, bricht die Kette.
    const link = target.closest('a[href]');
    if (target.closest(TeaNavTracking.SELECTORS.forceChainBreakers) || (link && this._breaksChain(link))) {
      this._log('Chain-Breaker:', link ? link.getAttribute('href') : target);
      this._breakChain();
    }
  }

  _onSwitchClick(button) {
    const mode = button.dataset.navMode;
    if (!mode) return; // Switch ohne Tracking-Modus (kein Tee-Layer)

    this._sessionSet(TeaNavTracking.MODE_KEY, mode);
    this._trackEvent('tea_nav_switch', { tea_nav_mode: mode });
    this._clarityTag('tea_nav_mode', mode);
    this._log('Switch-Klick -> mode:', mode);
  }

  _onSublinkClick(link) {
    const layer = link.closest('[data-nav-mode]');
    const mode = layer ? layer.dataset.navMode : null;

    // Sublink eines Dropdowns ohne Tee-Switch (z.B. Teaware) -> Kette brechen.
    if (!mode) {
      this._breakChain();
      return;
    }

    // Der Collection-Handle aus dem href ist sprachneutral und stabil und dient
    // direkt als Kategorie-Dimension in den Auswertungen.
    const collection = this._collectionFromHref(link.getAttribute('href'));

    this._writeAttribution({
      source: 'tea_switch',
      mode: mode,
      collection: collection,
      ts: Date.now(),
      intact: true
    });
    this._trackEvent('tea_nav_link_click', {
      tea_nav_mode: mode,
      tea_nav_collection: collection
    });
    this._clarityTag('tea_nav_mode', mode);
    this._log('Sublink-Klick -> mode:', mode, '| collection:', collection);
  }

  _breakChain() {
    const attribution = this._readAttribution();
    if (!attribution || !attribution.intact) return;

    attribution.intact = false;
    this._writeAttribution(attribution);
    this._log('Kette gebrochen.');
  }

  // Kein Bruch bei Produkt-Links (Collection -> PDP) und Anker-/JS-Controls,
  // alles andere (andere Collection, Logo, Footer, Suche, Pages ...) bricht.
  _breaksChain(link) {
    const href = link.getAttribute('href') || '';
    if (!href) return false;
    if (/^(#|javascript:|mailto:|tel:)/i.test(href)) return false;
    if (/\/products\//.test(href)) return false;
    return true;
  }

  // Add-to-Cart: Kontext wird bewusst NICHT geleert, damit mehrere Produkte aus
  // derselben Switch-Navigation alle attribuiert werden. Erst ein Chain-Breaker
  // oder ein neuer Sublink-Klick ändert/beendet den Kontext.
  _onVariantAdded(event) {
    const variant = event.detail && event.detail.variant;
    const variantId = variant ? variant.id || variant.variant_id : null;
    const attribution = this._readAttribution();

    if (!this._isIntact(attribution)) {
      this._log('Add-to-Cart NICHT attribuiert (Kontext leer/gebrochen/abgelaufen). variantId:', variantId);
      this._trackEvent('tea_nav_add_to_cart', {
        tea_nav_attributed: false,
        tea_nav_variant_id: variantId || null
      });
      return;
    }

    this._log('Add-to-Cart attribuiert -> mode:', attribution.mode, '| variantId:', variantId);
    this._trackEvent('tea_nav_add_to_cart', {
      tea_nav_attributed: true,
      tea_nav_mode: attribution.mode,
      tea_nav_collection: attribution.collection,
      tea_nav_variant_id: variantId || null
    });
    this._clarityTag('atc_attributed_mode', attribution.mode);
    this._writeCartAttribute(variantId, attribution.mode);
  }

  _readAttribution() {
    const raw = this._sessionGet(TeaNavTracking.ATTRIBUTION_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  _writeAttribution(attribution) {
    this._sessionSet(TeaNavTracking.ATTRIBUTION_KEY, JSON.stringify(attribution));
  }

  _isIntact(attribution) {
    if (!attribution || !attribution.intact) return false;
    if (attribution.source !== 'tea_switch') return false;
    return Date.now() - attribution.ts < TeaNavTracking.ATTRIBUTION_TTL_MS;
  }

  // GA4-Outbox: jedes Event landet zuerst in einer sessionStorage-Queue und wird
  // erst entfernt, wenn GTM den Empfang bestätigt (eventCallback). Offene Einträge
  // pusht die Folgeseite erneut (max. MAX_PUSH_ATTEMPTS Versuche, identifizierbar
  // über tea_nav_event_id).
  _trackEvent(eventName, params) {
    const entry = {
      id: Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8),
      event: eventName,
      params: params || {},
      attempts: 0
    };

    let queue = this._readQueue();
    if (queue.length >= TeaNavTracking.MAX_QUEUE_SIZE) {
      // Älteste Einträge verwerfen, damit die Queue nicht unbegrenzt wächst (z.B. wenn GTM geblockt ist).
      queue = queue.slice(queue.length - TeaNavTracking.MAX_QUEUE_SIZE + 1);
    }
    queue.push(entry);
    this._writeQueue(queue);

    this._pushQueueEntry(entry);
  }

  _pushQueueEntry(entry) {
    // Versuch VOR dem Push persistieren: unterbricht ein Seitenwechsel den Ablauf,
    // ist der Versuch bereits gezählt und es gibt kein endloses Re-Pushen.
    entry.attempts += 1;
    const queue = this._readQueue().map((queuedEntry) => {
      return queuedEntry.id === entry.id ? entry : queuedEntry;
    });
    this._writeQueue(queue);

    window.dataLayer = window.dataLayer || [];
    const payload = Object.assign({ event: entry.event }, entry.params, {
      tea_nav_event_id: entry.id,
      eventCallback: () => this._removeFromQueue(entry.id),
      eventTimeout: TeaNavTracking.EVENT_TIMEOUT_MS
    });
    window.dataLayer.push(payload);
    this._log('dataLayer.push ->', payload);
  }

  _flushEventQueue() {
    const openEntries = [];
    const deadEntries = [];
    this._readQueue().forEach((entry) => {
      if (entry.attempts >= TeaNavTracking.MAX_PUSH_ATTEMPTS) {
        deadEntries.push(entry);
      } else {
        openEntries.push(entry);
      }
    });

    if (deadEntries.length > 0) {
      this._writeQueue(openEntries);
      this._log('Events verworfen (max. Versuche erreicht):', deadEntries);
    }
    openEntries.forEach((entry) => this._pushQueueEntry(entry));
  }

  _removeFromQueue(entryId) {
    const queue = this._readQueue().filter((entry) => entry.id !== entryId);
    this._writeQueue(queue);
  }

  _readQueue() {
    const raw = this._sessionGet(TeaNavTracking.QUEUE_KEY);
    if (!raw) return [];
    try {
      const queue = JSON.parse(raw);
      return Array.isArray(queue) ? queue : [];
    } catch (error) {
      return [];
    }
  }

  _writeQueue(entries) {
    this._sessionSet(TeaNavTracking.QUEUE_KEY, JSON.stringify(entries));
  }

  _clarityTag(key, value) {
    if (typeof window.clarity !== 'function') return;
    try {
      window.clarity('set', key, value);
      this._log('Clarity set', key, '=', value);
    } catch (error) {
      this._log('Clarity-Fehler:', error);
    }
  }

  // Schreibt die Tee-Switch-Note pro Produkt in den Cart. /cart/update.js merged
  // Attribute (überschreibt die anderen Keys nicht), mehrere Notes koexistieren also.
  // keepalive stellt sicher, dass der Request einen sofortigen Seitenwechsel überlebt.
  _writeCartAttribute(variantId, mode) {
    const attributes = {};
    attributes['tea_nav_mode_' + (variantId || 'ts' + Date.now())] = mode;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
      keepalive: true
    }).then((response) => {
      this._log('cart/update.js Status:', response.status);
    }).catch((error) => {
      this._log('cart/update.js Fehler:', error);
    });
  }

  _collectionFromHref(href) {
    href = href || '';
    const collectionMatch = href.match(/\/collections\/([^\/?#]+)/);
    if (collectionMatch) return collectionMatch[1];

    // Fallback: letztes Pfadsegment (z.B. /pages/tee-quiz -> "tee-quiz")
    const path = href.replace(/[?#].*$/, '').replace(/\/+$/, '');
    return path.split('/').pop() || path;
  }

  _sessionGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  _sessionSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      // sessionStorage nicht verfügbar -> Tracking degradiert zu Fire-and-forget.
    }
  }

  _log(...args) {
    if (!TEA_NAV_TRACKING_DEBUG) return;
    console.log('%c[TeaNavTracking]', 'color:#0a7;font-weight:bold;', ...args);
  }
}

new TeaNavTracking();

/**
 * ------------------------------------------------------------------------------------------------------------
 * LAST VIEWED COLLECTION
 * ------------------------------------------------------------------------------------------------------------
 *
 * Web Component, die sich die zuletzt geöffnete Collection eines Nutzers für den aktuellen Tag merkt.
 *
 * Auf Collection-Seiten wird das Element mit den Collection-Daten (Handle, Titel, URL) gerendert und
 * schreibt diese beim Einhängen in den localStorage – zusammen mit dem heutigen Datum. Öffnet der Nutzer
 * mehrere Collections, gewinnt immer die zuletzt geöffnete.
 *
 * So lässt sich später (z.B. auf einer Produktseite) auslesen, aus welcher Collection der Nutzer zuletzt
 * kam. Die Erinnerung gilt bewusst nur für den aktuellen Tag: Ist der gespeicherte Eintrag von einem
 * früheren Tag, wird er als abgelaufen behandelt und nicht mehr zurückgegeben.
 *
 * Auslesen (an beliebiger Stelle, auch auf Seiten ohne dieses Element):
 *
 *   const collection = window.LastViewedCollection.get();
 *   // -> { handle, title, url } oder null, wenn heute noch keine Collection geöffnet wurde
 */
class LastViewedCollection extends HTMLElement {
  static STORAGE_KEY = 'pt_last_collection';

  connectedCallback() {
    const handle = this.dataset.handle;
    if (!handle) return; // Ohne Collection-Handle gibt es nichts zu merken.

    LastViewedCollection.set({
      handle: handle,
      title: this.dataset.title || '',
      url: this.dataset.url || ''
    });
  }

  // Speichert die Collection zusammen mit dem heutigen Datum.
  static set(collection) {
    const payload = {
      date: LastViewedCollection._today(),
      handle: collection.handle,
      title: collection.title,
      url: collection.url
    };

    try {
      localStorage.setItem(LastViewedCollection.STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      // localStorage nicht verfügbar (z.B. Private Mode) -> Erinnerung entfällt still.
    }
  }

  // Liefert die heute zuletzt geöffnete Collection oder null (auch bei einem Eintrag von einem früheren Tag).
  static get() {
    let raw = null;
    try {
      raw = localStorage.getItem(LastViewedCollection.STORAGE_KEY);
    } catch (error) {
      return null;
    }
    if (!raw) return null;

    let payload;
    try {
      payload = JSON.parse(raw);
    } catch (error) {
      return null;
    }

    if (!payload || payload.date !== LastViewedCollection._today()) return null;

    return {
      handle: payload.handle,
      title: payload.title,
      url: payload.url
    };
  }

  // Lokales Datum als YYYY-MM-DD (Tageswechsel in der Zeitzone des Nutzers, nicht UTC).
  static _today() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

if (!customElements.get('last-viewed-collection')) {
  customElements.define('last-viewed-collection', LastViewedCollection);
}

// Öffentlicher Lesezugriff für Code außerhalb dieser Datei (z.B. auf der Produktseite).
window.LastViewedCollection = LastViewedCollection;

/**
 * ------------------------------------------------------------------------------------------------------------
 * LAST VIEWED COLLECTION -> CART LINE ITEM PROPERTY
 * ------------------------------------------------------------------------------------------------------------
 *
 * Hängt bei JEDEM Add-to-Cart die zuletzt geöffnete Collection des Tages als verstecktes Line Item
 * Property "_last_viewed_collection" an die Warenkorb-Zeile an. Der führende Unterstrich blendet die
 * Property im Warenkorb/Checkout für den Kunden aus, sie bleibt aber in der Bestellung erhalten und
 * wird von Elevar automatisch in dessen Events übernommen.
 *
 * Umgesetzt als zentraler fetch-Wrapper, damit alle Add-to-Cart-Wege abgedeckt sind: das Produktformular
 * (FormData-Body) ebenso wie Quick-Buy/Upsells (JSON-Body). So bleibt das Vendor-Theme (theme.js)
 * unangetastet. Ist für den heutigen Tag keine Collection gemerkt, bleibt der Request unverändert.
 */
(function () {
  // Doppeltes Verdrahten verhindern, falls das Script mehrfach eingebunden wird.
  if (window.__lastViewedCollectionCartHook) return;
  window.__lastViewedCollectionCartHook = true;

  var PROPERTY_NAME = '_last_viewed_collection';
  var nativeFetch = window.fetch;
  if (typeof nativeFetch !== 'function') return;

  // Erkennt einen Add-to-Cart-Request (POST auf die – ggf. lokalisierte – /cart/add-Route).
  function isCartAddRequest(url, method) {
    if ((method || 'GET').toUpperCase() !== 'POST') return false;
    return typeof url === 'string' && /\/cart\/add(\.js)?($|\?)/.test(url);
  }

  // Ergänzt das Property im jeweiligen Body-Format (FormData beim Formular, JSON-String bei Quick-Buy).
  function appendCollectionProperty(init, handle) {
    if (init.body instanceof FormData) {
      init.body.append('properties[' + PROPERTY_NAME + ']', handle);
      return;
    }

    if (typeof init.body === 'string') {
      try {
        var payload = JSON.parse(init.body);
        payload.properties = Object.assign({}, payload.properties, { [PROPERTY_NAME]: handle });
        init.body = JSON.stringify(payload);
      } catch (error) {
        // Body ist kein JSON -> unverändert lassen.
      }
    }
  }

  window.fetch = function (input, init) {
    init = init || {};

    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = init.method || (input && input.method);

    if (isCartAddRequest(url, method)) {
      var lastCollection = window.LastViewedCollection && window.LastViewedCollection.get();
      if (lastCollection && lastCollection.handle) {
        appendCollectionProperty(init, lastCollection.handle);
      }
    }

    return nativeFetch.call(this, input, init);
  };
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * FlickityInert
 * ------------------------------------------------------------------------------------------------------------
 *
 * BFSG-Fix: Flickity (assets/flickity.js) setzt auf nicht aktive Slides aria-hidden="true", verwaltet aber
 * keinen Tab-Index. Fokussierbare Inhalte in ausgeblendeten Slides bleiben so per Tastatur erreichbar und
 * verletzen WCAG 4.1.2 ("ARIA hidden element must not be focusable or contain focusable elements").
 *
 * Diese Web Component hält inert synchron zum von Flickity gesetzten aria-hidden: Jede Slide-Cell mit
 * aria-hidden="true" bekommt zusätzlich inert und fällt damit aus Fokus- und Accessibility-Tree. Der
 * MutationObserver auf dem Karussell fängt jede aria-hidden-Änderung ab – unabhängig von Flickity-Internas
 * und inklusive Wrap-Around, Group-Cells und internem reload() beim Variantenwechsel.
 *
 * Als Geschwister-Element direkt nach </flickity-carousel> platzieren (analog zu <flickity-controls>) und per
 * controls="<carousel-id>" auf das Karussell referenzieren – NICHT innerhalb von <flickity-carousel>, da
 * Flickity direkte Kinder als Slides interpretiert.
 */
class FlickityInert extends HTMLElement {
  connectedCallback() {
    this._carousel = document.getElementById(this.getAttribute('controls'));
    if (!this._carousel) return;

    this._syncInert = this._syncInert.bind(this);

    // aria-hidden wird von Flickity auf den einzelnen Cells gesetzt -> subtree beobachten.
    this._observer = new MutationObserver(this._syncInert);
    this._observer.observe(this._carousel, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true
    });

    // Initialer Durchlauf, sobald Flickity aufgebaut hat (deckt die ersten aria-hidden-Setzungen ab).
    this._carousel.addEventListener('flickity:ready', this._syncInert);
    this._syncInert();
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
    }
    if (this._carousel) {
      this._carousel.removeEventListener('flickity:ready', this._syncInert);
    }
  }

  // Spiegelt aria-hidden auf inert: aktive Cell (kein aria-hidden) bleibt fokussierbar, alle anderen nicht.
  _syncInert() {
    this._carousel.querySelectorAll('.product__media-item').forEach((cell) => {
      cell.inert = cell.getAttribute('aria-hidden') === 'true';
    });
  }
}

if (!customElements.get('flickity-inert')) {
  customElements.define('flickity-inert', FlickityInert);
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * RegisterRedirect
 * ------------------------------------------------------------------------------------------------------------
 *
 * Web Component fuer die Register-Page (sections/main-customers-register.liquid).
 * Shopify leitet nach fehlerfreier Registrierung mit dem Marker "?registered=1" auf die
 * Register-Page zurueck. Nur dann – und nur wenn kein Fehler- oder Verifizierungsbanner
 * vorliegt – blendet die Component den Ladehinweis ein und leitet per JS auf das im
 * Theme-Editor eingestellte Ziel weiter. Beim ersten Aufruf ohne Marker passiert nichts.
 */
class RegisterRedirect extends HTMLElement {
  connectedCallback() {
    const params = new URLSearchParams(window.location.search);

    /* Ohne Marker wurde das Formular nicht abgesendet – keine Weiterleitung. */
    if (!params.has('registered')) return;

    /* Kam eine Fehler- oder Verifizierungsmeldung zurueck, bleibt die Seite mit Banner stehen. */
    if (this.querySelector('.form__banner')) return;

    /* Marker entfernen, damit ein spaeteres Neuladen keine erneute Weiterleitung ausloest. */
    params.delete('registered');
    const remainingQuery = params.toString();
    const cleanUrl = window.location.pathname + (remainingQuery ? '?' + remainingQuery : '') + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);

    /* Ladehinweis einblenden und auf das eingestellte Ziel weiterleiten. */
    const status = this.querySelector('[data-redirect-status]');
    if (status) status.hidden = false;

    window.location.assign(this.dataset.redirectUrl || '/account');
  }
}

if (!customElements.get('register-redirect')) {
  customElements.define('register-redirect', RegisterRedirect);
}

